import React, { useEffect, useState } from "react";
import { Pagination, Spinner, TooltipCustom } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Button, Col } from "react-bootstrap";
import TableHead from "./TableHead";
import {
    useGetReservationsQuery,
    GetReservationsQuery,
} from "@me-team/host/meBooking/restaurantBooking/graphql/restaurantBooking.hooks";
import { apolloClientMeBooking } from "@me-team/host/src/apollo/apollo-client";
import { ReservationSortField, Order, ReservationStatus } from "@me-team/host/meBooking/graphql/types";
import BookingModal from "./BookingModal";
import moment from "moment";
import { useCloseTooltipsOnModalOpen } from "@me-team/common/src/hooks/useCloseTooltipsOnModalOpen";
import { formattedPhone } from "../../utils/formattedPhone";

type ProcessRequestsProps = {
    statuses: ReservationStatus | ReservationStatus[];
    searchTerm?: string;
    dateTerm?: string;
    activeTab: string;
};
export type Reservation = NonNullable<NonNullable<GetReservationsQuery["reservations"]>["reservations"]>[0];

const BookingRequests: React.FC<ProcessRequestsProps> = ({ statuses, searchTerm, dateTerm, activeTab }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);

    const [reservationAtOrder, setReservationAtOrder] = useState(Order.Desc);
    const [createdAtOrder, setCreatedAtOrder] = useState(Order.Desc);
    const [activeSort, setActiveSort] = useState<"reservationAt" | "createdAt" | "status" | null>(null);

    const [statusOrder, setStatusOrder] = useState(Order.Asc);

    const toggleReservationAtOrder = () => {
        setActiveSort("reservationAt");
        setReservationAtOrder((prev) => (prev === Order.Desc ? Order.Asc : Order.Desc));
    };

    const toggleCreatedAtOrder = () => {
        setActiveSort("createdAt");
        setCreatedAtOrder((prev) => (prev === Order.Desc ? Order.Asc : Order.Desc));
    };
    const toggleStatusOrder = () => {
        setActiveSort("status");
        setStatusOrder((prev) => (prev === Order.Asc ? Order.Desc : Order.Asc));
    };

    const buildSort = () => {
        const baseSorts = [
            { sortField: ReservationSortField.Status, order: statusOrder },
            { sortField: ReservationSortField.CreatedAt, order: createdAtOrder },
            { sortField: ReservationSortField.ReservationAt, order: reservationAtOrder },
        ];

        const premiumSort = {
            sortField: ReservationSortField.Premium,
            order: Order.Desc,
        };

        if (!activeSort) {
            return [
                { sortField: ReservationSortField.Status, order: statusOrder },
                premiumSort,
                ...baseSorts.filter((s) => s.sortField !== ReservationSortField.Status),
            ];
        }

        const activeFieldMap = {
            createdAt: ReservationSortField.CreatedAt,
            reservationAt: ReservationSortField.ReservationAt,
            status: ReservationSortField.Status,
        };

        const activeField = activeFieldMap[activeSort];

        const activeSortObject = baseSorts.find((s) => s.sortField === activeField);

        const otherSorts = baseSorts.filter((s) => s.sortField !== activeField);

        return [activeSortObject!, premiumSort, ...otherSorts];
    };

    const { data, loading, refetch } = useGetReservationsQuery({
        variables: {
            statuses: Array.isArray(statuses) ? statuses : statuses ? [statuses] : undefined,
            page: currentPage,
            perPage: 20,
            search: searchTerm || undefined,
            date: dateTerm ? moment(dateTerm).format("YYYY-MM-DD") : undefined,
            sort: buildSort(),
        },
        client: apolloClientMeBooking,
    });

    useEffect(() => {
        refetch();
    }, [activeTab, refetch]);

    const firstItemIndex = (currentPage - 1) * (data?.reservations?.itemsPerPage ?? 0) + 1;
    const lastItemIndex = Math.min(
        currentPage * (data?.reservations?.itemsPerPage ?? 0),
        data?.reservations?.totalCount ?? 0
    );

    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();
    const [show, setShow] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

    const handleClose = () => {
        resetTooltips();
        setShow(false);
        setSelectedReservation(null);
    };

    const handleShow = (reservation: Reservation) => {
        resetTooltips();
        setSelectedReservation(reservation);
        setShow(true);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    return (
        <>
            <div className="table-responsive scrollbar mb-4">
                {!!data?.reservations?.reservations?.length ? (
                    <table className="table mt-6 mb-2 align-top">
                        <TableHead
                            toggleReservationAtOrder={toggleReservationAtOrder}
                            reservationAtOrder={reservationAtOrder}
                            toggleCreatedAtOrder={toggleCreatedAtOrder}
                            createdAtOrder={createdAtOrder}
                            toggleStatusOrder={toggleStatusOrder}
                            statusOrder={statusOrder}
                        />
                        <tbody className="fs-7">
                            {loading ? (
                                <tr>
                                    <td colSpan={10}>
                                        <Spinner />
                                    </td>
                                </tr>
                            ) : (
                                data?.reservations?.reservations.map((reservation, index) => (
                                    <tr key={reservation.id ?? index}>
                                        <td className="py-3 ps-lg-4">
                                            <span className="text-nowrap">
                                                {moment(reservation.createdAt).format("D MMMM,")}
                                            </span>{" "}
                                            <br />
                                            {moment(reservation.createdAt).format("h:mm")}
                                        </td>
                                        <td className="py-3">
                                            {reservation.status === "IN_PROGRESS" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-warning-dark text-warning-dark bg-warning-light text-nowrap">
                                                    {t("In progress")}
                                                </span>
                                            )}
                                            {reservation.status === "CONFIRMED" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-green text-green bg-additional-green">
                                                    {t("Confirmed")}
                                                </span>
                                            )}
                                            {reservation.status === "NO_CONNECTION" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-pink text-pink bg-additional-pink text-nowrap">
                                                    {t("No Connection")}
                                                </span>
                                            )}
                                            {reservation.status === "REJECTED" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-danger text-danger bg-danger-light">
                                                    {t("Rejected")}
                                                </span>
                                            )}
                                            {reservation.status === "CANCELLED_BY_USER" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-dark text-dark bg-additional-grey">
                                                    {t("Canceled")}
                                                </span>
                                            )}
                                            {reservation.status === "PENDING" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-dark text-dark bg-light-blue">
                                                    {t("Pending")}
                                                </span>
                                            )}
                                            {reservation.status === "WAITING_FOR_OPENING" && (
                                                <span className="fs-7 d-inline-block px-2 py-1 rounded border border-dark text-dark bg-additional-light-blue text-nowrap">
                                                    {t("Waiting for opening")}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3">{reservation.id}</td>
                                        <td className="py-3" style={{ maxWidth: 160, overflowWrap: "anywhere" }}>
                                            <b>{reservation.branch?.name ?? reservation.customPlace?.name}</b>
                                        </td>
                                        <td className="py-3 text-nowrap">
                                            <span className="me-1">
                                                {reservation.client?.name} {reservation.client?.surname}
                                            </span>
                                            {reservation.isPremium ? (
                                                <div className="bg-warning text-white rounded px-2 py-1 d-inline-block">
                                                    <i className="bi bi-award"></i>
                                                </div>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                        <td className="py-3">
                                            {formattedPhone(reservation.client?.phone)}
                                            <br />
                                            {reservation.client?.email}
                                        </td>
                                        <td className="py-3">
                                            {moment(reservation.reservationAt).format("D MMMM, YYYY")} <br />
                                            {moment(reservation.reservationAt).format("h:mm A")}
                                            {reservation.flexibility !== "NONE" && (
                                                <span className="ms-2 d-inline-block px-2 rounded bg-additional-violet text-primary">
                                                    ± {reservation.flexibility.slice(5, 6)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3">{reservation.guestsCount}</td>
                                        <td className="py-3" style={{ maxWidth: 160, overflowWrap: "anywhere" }}>
                                            {reservation.comment ? reservation.comment : "–"}
                                        </td>
                                        <td className="py-3 text-lg-end pe-lg-4 px-0 px-lg-2">
                                            <div className="d-flex align-items-center justify-content-end">
                                                {reservation.moderatorComment && (
                                                    <TooltipCustom
                                                        placement="bottom"
                                                        text={reservation.moderatorComment}
                                                    >
                                                        <i className="bi bi-info-circle me-lg-3 me-2"></i>
                                                    </TooltipCustom>
                                                )}
                                                <TooltipCustom
                                                    placement="bottom"
                                                    text={t("Details")}
                                                    key={tooltipResetKey}
                                                >
                                                    <Button
                                                        onClick={() => handleShow(reservation)}
                                                        variant="outline-secondary"
                                                        className="btn-size outline-secondary text-dark p-0"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </Button>
                                                </TooltipCustom>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                ) : (
                    <div className="w-100 justify-content-md-center">
                        <Col md={10} className="mt-5 pt-5 mx-auto">
                            <p className="fs-5 text-center font-weight-bold">
                                {t("No data found for the specified parameters.")}
                            </p>
                        </Col>
                    </div>
                )}
            </div>

            {data?.reservations?.reservations?.length ? (
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Pagination
                        t={t}
                        pages={parseInt(data?.reservations.pageCount ?? "0")}
                        currentPage={data?.reservations.currentPage ?? 1}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={data?.reservations.totalCount ?? 0}
                    />
                </div>
            ) : null}

            <BookingModal
                show={show}
                handleClose={handleClose}
                reservation={selectedReservation}
                refetchReservations={refetch}
            />
        </>
    );
};

export default BookingRequests;
